/**
 * The 44px column at the end of a watched site's row: open, check now, pause or
 * resume, edit, remove.
 *
 * Everything past "open" and "visit" is a write and answers to
 * `manage:crm.monitoring`. For somebody without that key the entries are not
 * drawn at all rather than drawn disabled: a greyed "Remove" invites a click and
 * a question, and the menu still has the two things that person can do.
 */
import { Link } from "react-router-dom";
import { ExternalLink, Loader2, MoreHorizontal, Pause, Pencil, Play, RefreshCw, Trash2, ArrowUpRight } from "lucide-react";
import { cn } from "@wa/ui";
import { notChecked, type MonitorRow } from "../../../lib/monitoring";
import { Pop, useClosePops } from "../../shell/Pop";
import type { T } from "./words";

export function MonitorRowMenu({
  site,
  canManage,
  engineOn,
  busy,
  t,
  onCheck,
  onPause,
  onEdit,
  onRemove,
}: {
  site: MonitorRow;
  canManage: boolean;
  /** The engine switch: while it is off nothing is checked, on request either. */
  engineOn: boolean;
  busy: boolean;
  t: T;
  onCheck: () => void;
  onPause: (paused: boolean) => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const close = useClosePops();
  const act = (fn: () => void) => {
    close();
    fn();
  };

  return (
    // Above the row's stretched link, or the menu button would open the page.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb", open && "is-on")}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
            disabled={busy}
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : <MoreHorizontal className="size-4" />}
          </button>
        )}
      >
        <Link className="crm-mi" to={`/monitoring/${site.id}`} onClick={close}>
          <ArrowUpRight className="size-4" />
          {t("monitoring.menu.open")}
        </Link>
        <a className="crm-mi" href={site.url} target="_blank" rel="noreferrer noopener" onClick={close}>
          <ExternalLink className="size-4" />
          {t("monitoring.menu.visit")}
        </a>
        {canManage ? (
          <>
            <div className="crm-menu-sep" />
            {/* A site the engine does not check (paused, its client inactive, or
                the engine switched off) gets no "check now": the server refuses
                it, and the result would be one nothing acts on. */}
            {engineOn && !notChecked(site) ? (
              <button type="button" className="crm-mi" onClick={() => act(onCheck)}>
                <RefreshCw className="size-4" />
                {t("monitoring.menu.check")}
              </button>
            ) : null}
            <button type="button" className="crm-mi" onClick={() => act(() => onPause(!site.paused))}>
              {site.paused ? <Play className="size-4" /> : <Pause className="size-4" />}
              {t(site.paused ? "monitoring.menu.resume" : "monitoring.menu.pause")}
            </button>
            <button type="button" className="crm-mi" onClick={() => act(onEdit)}>
              <Pencil className="size-4" />
              {t("monitoring.menu.edit")}
            </button>
            <div className="crm-menu-sep" />
            <button type="button" className="crm-mi crm-mi-danger" onClick={() => act(onRemove)}>
              <Trash2 className="size-4" />
              {t("monitoring.menu.remove")}
            </button>
          </>
        ) : null}
      </Pop>
    </span>
  );
}
